import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CalendarDays, CheckCircle2, Circle, Download, Flag, Sparkles, Users } from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { AsyncSection } from '@/app/components/AsyncSection';
import { SubmitError } from '@/app/components/SubmitError';
import { Alert } from '@/app/components/ui/Alert';
import { Badge } from '@/app/components/ui/Badge';
import { Button } from '@/app/components/ui/Button';
import { Card, CardHeader } from '@/app/components/ui/Card';
import { Field, Textarea } from '@/app/components/ui/Field';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { NotFoundPage } from '@/app/pages/NotFoundPage';
import { useMe } from '@/features/me/MeContext';
import {
  addPilotEvaluation,
  fetchPilotParticipantCount,
  fetchPilotStatistics,
  getPilot,
  getPilotEvaluations,
  setPilotRecommendation,
  setPilotStatus,
  type PilotBaselineDelta,
  type PilotKpi,
} from '@/features/pilots/api';
import { pilotOutcome, type PilotOutcome } from '@/features/pilots/outcome';
import { statusTone } from '@/features/pilots/statusTone';
import {
  EVALUATION_PHASES,
  PHASE_STATUS,
  PILOT_TRANSITIONS,
  type EvaluationPhase,
  type Pilot,
} from '@/features/pilots/types';
import { formatDate, formatNumber } from '@/lib/format';
import { useAsync } from '@/lib/useAsync';
import { useSubmit } from '@/lib/useSubmit';

const KPI_UNIT_SUFFIX: Record<PilotKpi['unit'], string> = {
  percent: '%',
  count: '',
  score: '/10',
};

/** A locally-kept evaluation entry — see `EvaluationsSection`'s doc for why
 * `recommendations` (unlike `conclusions`) never reaches the API. */
interface LocalEvaluation {
  id: string;
  phase: EvaluationPhase;
  conclusions: string;
  recommendations: string;
  created_at: string;
}

/**
 * `/pilots/:pilotId` — the hero (status + AI-samenvatting + advies), 4
 * kerncijfer-tegels, the pilot info card with its 3 fase-cards, 8 KPI cards,
 * a vitality/activity graph, the Conclusies-/Aanbevelingen-lists, and the
 * Verlengen/Opschalen decision block.
 */
export function PilotDetailPage() {
  const { pilotId } = useParams<{ pilotId: string }>();
  const { t } = useTranslation();
  const { permissions } = useMe();
  // useAsync must run every render regardless of pilotId — an empty string
  // is a harmless key here since the NotFoundPage branch below never
  // renders the AsyncSection that would use its result.
  const pilot = useAsync(() => getPilot(pilotId ?? ''), [pilotId]);

  if (!pilotId) return <NotFoundPage />;

  const canManage = (companyId: string) =>
    permissions.isAdmin || permissions.administeredCompanyIds.includes(companyId);

  return (
    <section className="page flex flex-col gap-6">
      <AsyncSection state={pilot} label={t('pilots.detail.title')} skeleton="card">
        {(data) => (
          <PilotDetail
            pilot={data}
            canManage={canManage(data.company_id)}
            onReload={pilot.reload}
          />
        )}
      </AsyncSection>
    </section>
  );
}

function PilotDetail({
  pilot,
  canManage,
  onReload,
}: {
  pilot: Pilot;
  canManage: boolean;
  onReload: () => void;
}) {
  const { t } = useTranslation();
  const overview = useAsync(
    () =>
      Promise.all([
        fetchPilotStatistics(pilot.id),
        fetchPilotParticipantCount(pilot.department_ids),
      ]).then(([statistics, participantCount]) => ({ statistics, participantCount })),
    [pilot.id, pilot.department_ids.join(',')],
  );
  // Persisted evaluations (`conclusions` only — the API has no field for
  // "aanbevelingen") are fetched once per pilot. A freshly added one is kept
  // in `addedEvaluations` too, keyed by the same id the POST returned, so its
  // recommendations half (never persisted) survives the `reload()` this
  // triggers — merging by id (added wins) avoids showing it twice once the
  // server list catches up.
  const serverEvaluations = useAsync(() => getPilotEvaluations(pilot.id), [pilot.id]);
  const [addedEvaluations, setAddedEvaluations] = useState<LocalEvaluation[]>([]);
  const evaluationsById = new Map<string, LocalEvaluation>();
  for (const evaluation of serverEvaluations.data ?? []) {
    evaluationsById.set(evaluation.id, {
      id: evaluation.id,
      phase: evaluation.phase,
      conclusions: evaluation.conclusions ?? '',
      recommendations: '',
      created_at: evaluation.created_at,
    });
  }
  for (const evaluation of addedEvaluations) evaluationsById.set(evaluation.id, evaluation);
  const evaluations = [...evaluationsById.values()];

  return (
    <>
      <PageHeader
        title={t('pilots.detail.title')}
        lead={t('pilots.detail.lead')}
        actions={
          <Button variant="secondary" onClick={() => window.print()}>
            <Download className="size-4" aria-hidden="true" />
            {t('pilots.detail.exportPdf')}
          </Button>
        }
      />

      <AsyncSection state={overview} label={t('pilots.detail.title')} skeleton="card">
        {({ statistics, participantCount }) => (
          <div className="flex flex-col gap-6">
            <HeroCard kpis={statistics.kpis} />
            <SummaryTiles kpis={statistics.kpis} deltas={statistics.baselineDeltas} />

            <AiSummaryCard pilot={pilot} canManage={canManage} onSaved={onReload} />

            <PilotInfoCard
              pilot={pilot}
              participantCount={participantCount}
              evaluations={evaluations}
              canManage={canManage}
              onDone={onReload}
            />

            <Card>
              <CardHeader title={t('pilots.detail.kpisTitle')} />
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {statistics.kpis.map((kpi) => (
                  <KpiCard key={kpi.id} kpi={kpi} />
                ))}
              </div>
            </Card>

            <VitalityActivityChart kpis={statistics.kpis} />

            <EvaluationsSection
              pilot={pilot}
              canManage={canManage}
              evaluations={evaluations}
              onAdded={(evaluation) => {
                setAddedEvaluations((current) => [...current, evaluation]);
                serverEvaluations.reload();
              }}
            />

            <DecisionBlock
              pilot={pilot}
              outcome={pilotOutcome(statistics.kpis)}
              canManage={canManage}
              onDone={onReload}
            />
          </div>
        )}
      </AsyncSection>
    </>
  );
}

const HERO_TONE: Record<PilotOutcome | 'pending', 'mint' | 'warning' | 'outline'> = {
  success: 'mint',
  partial: 'warning',
  atRisk: 'warning',
  pending: 'outline',
};

function HeroCard({ kpis }: { kpis: PilotKpi[] }) {
  const { t } = useTranslation();
  const outcome = pilotOutcome(kpis);
  const key = outcome ?? 'pending';

  return (
    <Card className="bg-mint-tint">
      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={HERO_TONE[key]}>{t(`pilots.detail.hero.badge.${key}`)}</Badge>
        <span className="text-accent text-sm">{t(`pilots.detail.hero.goals.${key}`)}</span>
      </div>
      <p className="font-heading text-ink mt-3 text-xl leading-snug font-semibold">
        {t(`pilots.detail.hero.headlinePrefix.${key}`)}{' '}
        <span className="text-mint-deep">{t(`pilots.detail.hero.headlineAdvice.${key}`)}</span>
      </p>
    </Card>
  );
}

function summaryValue(kpi: PilotKpi | undefined): string | null {
  if (!kpi || kpi.status !== 'ok' || kpi.value === null) return null;
  return `${formatNumber(kpi.value)}${KPI_UNIT_SUFFIX[kpi.unit]}`;
}

function SummaryTiles({ kpis, deltas }: { kpis: PilotKpi[]; deltas: PilotBaselineDelta[] }) {
  const { t } = useTranslation();
  const participation = kpis.find((k) => k.id === 'participationRate');
  const dailyActive = kpis.find((k) => k.id === 'dailyUsage');
  const stress = deltas.find((d) => d.id === 'stress');
  const sleep = deltas.find((d) => d.id === 'sleep');

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <SummaryTile
        label={t('pilots.detail.summary.participation')}
        value={summaryValue(participation)}
        sub={
          participation?.target !== null && participation?.target !== undefined
            ? t('pilots.detail.summary.goal', { value: formatNumber(participation.target) })
            : undefined
        }
      />
      <SummaryTile
        label={t('pilots.detail.summary.dailyActive')}
        value={summaryValue(dailyActive)}
        sub={
          dailyActive?.target !== null && dailyActive?.target !== undefined
            ? t('pilots.detail.summary.goal', { value: formatNumber(dailyActive.target) })
            : undefined
        }
      />
      <SummaryTile
        label={t('pilots.detail.summary.stress')}
        value={
          stress?.status === 'ok' && stress.value !== null
            ? `${formatNumber(stress.value, { signDisplay: 'exceptZero' })}%`
            : null
        }
        sub={t('pilots.detail.summary.vsBaseline')}
      />
      <SummaryTile
        label={t('pilots.detail.summary.sleep')}
        value={
          sleep?.status === 'ok' && sleep.value !== null
            ? t('pilots.detail.summary.minutes', {
                value: formatNumber(sleep.value, { signDisplay: 'exceptZero' }),
              })
            : null
        }
        sub={t('pilots.detail.summary.vsBaseline')}
      />
    </div>
  );
}

function SummaryTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | null;
  sub?: string | undefined;
}) {
  const { t } = useTranslation();
  return (
    <div className="border-line flex flex-col gap-1 rounded-2xl border p-4">
      <span className="text-accent text-[13px]">{label}</span>
      <span className="font-heading text-ink text-[22px] font-semibold">
        {value ?? t('pilots.detail.noData')}
      </span>
      {value !== null && sub && <span className="text-accent text-[13px]">{sub}</span>}
    </div>
  );
}

function AiSummaryCard({
  pilot,
  canManage,
  onSaved,
}: {
  pilot: Pilot;
  canManage: boolean;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(pilot.recommendation ?? '');
  const form = useSubmit(async () => {
    await setPilotRecommendation(pilot.id, text);
    setEditing(false);
    onSaved();
  });

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Sparkles className="text-mint-deep size-4" aria-hidden="true" />
            {t('pilots.detail.aiSummaryTitle')}
          </span>
        }
        action={
          canManage && !editing ? (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              {t('pilots.actions.edit')}
            </Button>
          ) : undefined
        }
      />
      {editing ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void form.submit();
          }}
        >
          <Field label={t('pilots.detail.aiSummaryTitle')}>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={form.pending}>
              {t('pilots.actions.save')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>
              {t('pilots.actions.cancel')}
            </Button>
          </div>
          <SubmitError error={form.error} />
        </form>
      ) : (
        <p className="text-ink text-sm whitespace-pre-wrap">
          {pilot.recommendation && pilot.recommendation.trim() !== ''
            ? pilot.recommendation
            : t('pilots.detail.recommendationEmpty')}
        </p>
      )}
    </Card>
  );
}

function PilotInfoCard({
  pilot,
  participantCount,
  evaluations,
  canManage,
  onDone,
}: {
  pilot: Pilot;
  participantCount: number;
  evaluations: LocalEvaluation[];
  canManage: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-heading text-ink text-lg font-semibold">{pilot.name}</h2>
            <Badge tone={statusTone(pilot.status)}>{t(`pilots.status.${pilot.status}`)}</Badge>
          </div>
        </div>
        <div className="text-accent flex flex-wrap items-center gap-4 text-sm">
          {pilot.start_date && (
            <span className="flex items-center gap-1.5">
              <CalendarDays className="size-4" aria-hidden="true" />
              {formatDate(pilot.start_date)}
              {pilot.end_date ? ` – ${formatDate(pilot.end_date)}` : ''}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Users className="size-4" aria-hidden="true" />
            {t('pilots.detail.participantCount', { count: participantCount })}
          </span>
        </div>
      </div>

      <div className="border-line mt-5 border-t pt-5">
        <PhaseButtons
          pilot={pilot}
          evaluations={evaluations}
          canManage={canManage}
          onDone={onDone}
        />
      </div>
    </Card>
  );
}

function PhaseButtons({
  pilot,
  evaluations,
  canManage,
  onDone,
}: {
  pilot: Pilot;
  evaluations: LocalEvaluation[];
  canManage: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const form = useSubmit(async (status: Pilot['status']) => {
    await setPilotStatus(pilot.id, status);
    onDone();
  });

  const phaseDate = (phase: EvaluationPhase) => {
    if (phase === 'baseline') return pilot.start_date;
    const evaluation = evaluations.find((e) => e.phase === phase);
    return evaluation?.created_at ?? null;
  };

  return (
    <div className="flex flex-wrap gap-3">
      {EVALUATION_PHASES.map((phase) => {
        const targetStatus = PHASE_STATUS[phase];
        const reached =
          pilot.status === targetStatus || !PILOT_TRANSITIONS[targetStatus].includes(pilot.status);
        const canAdvance = canManage && PILOT_TRANSITIONS[pilot.status].includes(targetStatus);
        const date = phaseDate(phase);
        const Icon = phase === 'final' ? Flag : reached ? CheckCircle2 : Circle;
        return (
          <div
            key={phase}
            className="border-line flex min-w-40 flex-col gap-1 rounded-2xl border p-3"
          >
            <span className={reached ? 'text-mint-deep' : 'text-accent'}>
              <Icon className="size-5" aria-hidden="true" />
            </span>
            <Button
              variant={reached ? 'secondary' : 'primary'}
              size="sm"
              disabled={!canAdvance || form.pending}
              onClick={() => void form.submit(targetStatus)}
            >
              {t(`pilots.phases.${phase}`)}
            </Button>
            {date && <span className="text-accent text-[13px]">{formatDate(date)}</span>}
          </div>
        );
      })}
      <SubmitError error={form.error} />
    </div>
  );
}

function KpiCard({ kpi }: { kpi: PilotKpi }) {
  const { t } = useTranslation();
  const progress =
    kpi.status === 'ok' && kpi.value !== null && kpi.target !== null && kpi.target > 0
      ? Math.min(100, Math.round((kpi.value / kpi.target) * 100))
      : null;

  return (
    <div className="border-line flex flex-col gap-2 rounded-2xl border p-4">
      <span className="text-accent text-[13px]">{t(`pilots.kpis.${kpi.id}`)}</span>
      {kpi.status === 'unavailable' ? (
        <span className="text-ink text-sm">{t('pilots.detail.noData')}</span>
      ) : (
        <>
          <span className="font-heading text-ink text-[22px] font-semibold">
            {kpi.value}
            {KPI_UNIT_SUFFIX[kpi.unit]}
            {kpi.target !== null && (
              <span className="text-accent text-[13px] font-normal">
                {' '}
                / {kpi.target}
                {KPI_UNIT_SUFFIX[kpi.unit]}
              </span>
            )}
          </span>
          {progress !== null && (
            <div className="bg-paper border-line h-2 w-full overflow-hidden rounded-full border">
              <div className="bg-mint h-full" style={{ width: `${progress}%` }} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function VitalityActivityChart({ kpis }: { kpis: PilotKpi[] }) {
  const { t } = useTranslation();
  const vitality = kpis.find((k) => k.id === 'avgVitality');
  const activity = kpis.find((k) => k.id === 'activeUsers');
  const hasData = vitality?.status === 'ok' || activity?.status === 'ok';

  const data = hasData
    ? [
        {
          name: t('pilots.detail.chartCurrent'),
          vitality: vitality?.value ?? 0,
          activity: activity?.value ?? 0,
        },
      ]
    : [];

  return (
    <Card>
      <CardHeader title={t('pilots.detail.chartTitle')} />
      {!hasData ? (
        <p className="text-ink text-sm">{t('pilots.detail.noData')}</p>
      ) : (
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="vitality" stroke="var(--color-mint)" />
              <Line type="monotone" dataKey="activity" stroke="var(--color-accent)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function EvaluationsSection({
  pilot,
  canManage,
  evaluations,
  onAdded,
}: {
  pilot: Pilot;
  canManage: boolean;
  evaluations: LocalEvaluation[];
  onAdded: (evaluation: LocalEvaluation) => void;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<EvaluationPhase>('baseline');
  const [conclusions, setConclusions] = useState('');
  const [recommendations, setRecommendations] = useState('');
  // `PilotEvaluation` (the API's own schema) has no `recommendations`
  // field — only `conclusions` is persisted (`GET /pilots/{id}/evaluations`
  // hydrates that half after a reload, see `PilotDetail`'s
  // `serverEvaluations`). `recommendations` itself stays local-only for the
  // Conclusies/Aanbevelingen split and does not survive a reload.
  const form = useSubmit(async () => {
    const { evaluation_id } = await addPilotEvaluation(pilot.id, { phase, conclusions });
    onAdded({
      id: evaluation_id,
      phase,
      conclusions,
      recommendations,
      created_at: new Date().toISOString(),
    });
    setConclusions('');
    setRecommendations('');
  });

  return (
    <Card>
      <CardHeader title={t('pilots.detail.evaluationsTitle')} />
      {evaluations.length === 0 ? (
        <p className="text-ink text-sm">{t('pilots.detail.evaluationsEmpty')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <h3 className="text-ink mb-2 text-sm font-semibold">
              {t('pilots.detail.conclusionsTitle')}
            </h3>
            <ul className="flex flex-col gap-3">
              {evaluations
                .filter((e) => e.conclusions.trim() !== '')
                .map((evaluation) => (
                  <li key={evaluation.id} className="border-line rounded-xl border p-3">
                    <Badge tone="outline">{t(`pilots.phases.${evaluation.phase}`)}</Badge>
                    <p className="text-ink mt-2 text-sm whitespace-pre-wrap">
                      {evaluation.conclusions}
                    </p>
                  </li>
                ))}
            </ul>
          </div>
          <div>
            <h3 className="text-ink mb-2 text-sm font-semibold">
              {t('pilots.detail.recommendationsListTitle')}
            </h3>
            <ul className="flex flex-col gap-3">
              {evaluations
                .filter((e) => e.recommendations.trim() !== '')
                .map((evaluation) => (
                  <li key={evaluation.id} className="border-line rounded-xl border p-3">
                    <Badge tone="outline">{t(`pilots.phases.${evaluation.phase}`)}</Badge>
                    <p className="text-ink mt-2 text-sm whitespace-pre-wrap">
                      {evaluation.recommendations}
                    </p>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      )}
      {canManage && (
        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void form.submit();
          }}
        >
          <Field label={t('pilots.detail.evaluationPhase')}>
            <select
              className="border-line bg-paper h-9 rounded-[0.5rem] border px-3 text-sm"
              value={phase}
              onChange={(e) => setPhase(e.target.value as EvaluationPhase)}
            >
              {EVALUATION_PHASES.map((p) => (
                <option key={p} value={p}>
                  {t(`pilots.phases.${p}`)}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label={t('pilots.detail.evaluationConclusions')}>
              <Textarea
                value={conclusions}
                onChange={(e) => setConclusions(e.target.value)}
                rows={3}
              />
            </Field>
            <Field label={t('pilots.detail.evaluationRecommendations')}>
              <Textarea
                value={recommendations}
                onChange={(e) => setRecommendations(e.target.value)}
                rows={3}
              />
            </Field>
          </div>
          <div>
            <Button type="submit" size="sm" disabled={form.pending}>
              {t('pilots.actions.addEvaluation')}
            </Button>
          </div>
          <SubmitError error={form.error} />
        </form>
      )}
    </Card>
  );
}

function DecisionBlock({
  pilot,
  outcome,
  canManage,
  onDone,
}: {
  pilot: Pilot;
  outcome: PilotOutcome | null;
  canManage: boolean;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const form = useSubmit(async (status: Pilot['status']) => {
    await setPilotStatus(pilot.id, status);
    onDone();
  });

  const canExtend = canManage && pilot.status !== 'scaled';
  const canScale = canManage && PILOT_TRANSITIONS[pilot.status].includes('scaled');
  const key = outcome ?? 'pending';

  if (!canManage) return null;

  return (
    <Card className="border-mint bg-mint-tint">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Flag className="text-mint-deep mt-0.5 size-5" aria-hidden="true" />
          <div>
            <p className="text-accent text-[13px]">{t('pilots.detail.decisionTitle')}</p>
            <p className="font-heading text-ink text-base font-semibold">
              {t(`pilots.detail.hero.headlineAdvice.${key}`)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="secondary"
            disabled={!canExtend}
            onClick={() => void form.submit(pilot.status)}
          >
            {t('pilots.actions.extend')}
          </Button>
          <Button disabled={!canScale || form.pending} onClick={() => void form.submit('scaled')}>
            {t('pilots.actions.scale')}
          </Button>
        </div>
      </div>
      {!canScale && pilot.status !== 'scaled' && (
        <Alert tone="info" className="mt-3">
          {t('pilots.detail.scaleRequiresCompleted')}
        </Alert>
      )}
      <SubmitError error={form.error} />
    </Card>
  );
}
